/**
 * Future Expense Row Component
 * 
 * Displays a future expense goal in a row format (instead of card).
 * Requested by client in PDF: Correções Librattum 07122025
 */
import { useMemo, useState } from 'react';
import { format, parseISO, differenceInMonths, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CurrencyDisplay } from './CurrencyDisplay';
import { ProgressIndicator } from './ProgressIndicator';
import { MoreVertical, Calendar, Check, Edit, Trash2, PiggyBank, Clock } from 'lucide-react';
import type { FutureExpense, FutureExpenseStatus } from '@/types/financial';

interface FutureExpenseRowProps {
  expense: FutureExpense;
  onRealize?: () => void;
  realizeTourId?: string;
  disableRealize?: boolean;
  onReplan?: () => void;
  onReplanFromSurplus?: () => void;
  onAddAsRevenue?: () => void;
  onDeleteFromSurplus?: () => void;
  onExecute?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onEdit?: () => void;
  onEditRealized?: () => void;
  onDelete?: () => void;
  onViewDetails?: () => void;
  referenceDate?: string | Date;
  className?: string;
}

type DisplayFutureExpenseStatus =
  | FutureExpenseStatus
  | 'value' // Valor atingido antes do prazo
  | 'concluded'; // Valor atingido e prazo alcançado (sem necessidade de novo enum no backend)

const statusConfig: Record<DisplayFutureExpenseStatus, { label: string; color: string; border: string; card?: string }> = {
  active: {
    label: 'Ativa',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    border: 'border-l-primary',
  },
  expired: {
    label: 'Expirado',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    border: 'border-l-red-500',
    card: 'bg-red-100/70 dark:bg-red-950/30',
  },
  value_reached: {
    // Status do backend (mantido por compatibilidade). No UI, preferimos "Valor"/"Concluído".
    label: 'Valor',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    border: 'border-l-yellow-500',
    card: 'bg-yellow-100/70 dark:bg-yellow-950/30',
  },
  value: {
    label: 'Valor',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    border: 'border-l-yellow-500',
    card: 'bg-yellow-100/70 dark:bg-yellow-950/30',
  },
  concluded: {
    label: 'Concluído',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    border: 'border-l-emerald-500',
    card: 'bg-emerald-100/70 dark:bg-emerald-950/30',
  },
  completed: {
    // Quando a meta já foi executada
    label: 'Executada',
    color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
    border: 'border-l-teal-500',
  },
  cancelled: {
    label: 'Cancelada',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    border: 'border-l-gray-500',
  },
};

export function FutureExpenseRow({
  expense,
  onRealize,
  realizeTourId,
  disableRealize = false,
  onReplan,
  onReplanFromSurplus,
  onAddAsRevenue,
  onDeleteFromSurplus,
  onExecute,
  onPause,
  onResume,
  onEdit,
  onEditRealized,
  onDelete,
  onViewDetails,
  referenceDate,
  className,
}: Readonly<FutureExpenseRowProps>) {
  const [isPositiveBalanceDialogOpen, setIsPositiveBalanceDialogOpen] = useState(false);
  const [positiveBalanceAction, setPositiveBalanceAction] = useState<'replan' | 'revenue' | 'delete'>('replan');

  const targetDate = useMemo(() => parseISO(expense.target_date), [expense.target_date]);
  const isDeadlineReached = useMemo(() => !isBefore(new Date(), targetDate), [targetDate]);

  const currentValue = Number(expense.accumulated_amount) || 0;
  const targetAmount = Number(expense.target_amount) || 0;
  const monthlyPayment = Number(expense.planned_contribution ?? expense.monthly_payment) || 0;
  const realizedContribution = Number(expense.realized_contribution) || 0;
  
  // Verifica se já houve um investimento no PO atual
  const hasInvestedInCurrentPeriod = realizedContribution > 0;

  const currentCents = Math.round(currentValue * 100);
  const targetCents = Math.round(targetAmount * 100);
  const hasReachedValueByAmount = targetCents > 0 && currentCents >= targetCents;
  const hasReachedExactValue = targetCents > 0 && currentCents === targetCents;
  const hasReachedValueFromStatus = expense.status === 'value_reached';

  const displayStatus: DisplayFutureExpenseStatus = useMemo(() => {
    if (expense.status === 'completed' || expense.status === 'cancelled') {
      return expense.status;
    }
    if (expense.status === 'expired') {
      return 'expired';
    }
    if (isDeadlineReached) {
      return hasReachedValueByAmount || hasReachedValueFromStatus ? 'concluded' : 'expired';
    }
    if (hasReachedValueByAmount) {
      return hasReachedExactValue ? 'concluded' : 'value';
    }
    if (hasReachedValueFromStatus) {
      return 'value';
    }
    return 'active';
  }, [
    expense.status,
    isDeadlineReached,
    hasReachedExactValue,
    hasReachedValueByAmount,
    hasReachedValueFromStatus,
  ]);

  const statusInfo = statusConfig[displayStatus] || statusConfig.active;
  const baseDate = useMemo(() => {
    if (!referenceDate) return new Date();
    if (referenceDate instanceof Date) return referenceDate;
    const parsed = parseISO(referenceDate);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [referenceDate]);
  const monthsRemaining = Math.max(0, differenceInMonths(targetDate, baseDate));
  
  const progress = expense.progress_percentage || 
    (targetAmount > 0 ? (currentValue / targetAmount) * 100 : 0);
  const isActive = displayStatus === 'active';
  const shouldShowActionDialog = ['value', 'concluded', 'expired'].includes(displayStatus);
  const isRealizeDisabled = disableRealize;
  const actionDialogMessage = useMemo(() => {
    switch (displayStatus) {
      case 'concluded':
        return 'Seu planejamento foi alcançado com sucesso!';
      case 'value':
        return 'Você possui despesa futura que atingiu o valor desejado.';
      case 'expired':
        return 'Você possui despesa futura que atingiu o prazo de conclusão, porém não alcançou o valor desejado';
      default:
        return 'Seu planejamento foi alcançado com sucesso!';
    }
  }, [displayStatus]);

  return (
    <Card
      className={cn(
        'border-l-4 overflow-hidden transition-shadow hover:shadow-md',
        statusInfo.card,
        statusInfo.border,
        className
      )}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
          {/* Top/Left section - Icon and info */}
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 text-primary shrink-0">
              <PiggyBank className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="font-medium truncate text-sm sm:text-base">{expense.name}</h4>
                {expense.category && (
                  <Badge variant="outline" className="text-[10px] sm:text-xs shrink-0">
                    {expense.category}
                  </Badge>
                )}
                {/* Actions dropdown - mobile: top-right */}
                <div className="sm:hidden ml-auto shrink-0">
                  {shouldShowActionDialog ? (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="h-7 px-2 text-xs font-medium"
                      onClick={() => setIsPositiveBalanceDialogOpen(true)}
                    >
                      <MoreVertical className="h-3.5 w-3.5 mr-1" />
                      Ações
                    </Button>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        {isActive ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Ações da meta">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="default" size="sm" className="h-7 px-2 text-xs font-medium">
                            <MoreVertical className="h-3.5 w-3.5 mr-1" />
                            Ações
                          </Button>
                        )}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {onEdit && (
                          <DropdownMenuItem onClick={onEdit}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar Planejado
                          </DropdownMenuItem>
                        )}
                        {onEditRealized && hasInvestedInCurrentPeriod && (
                          <DropdownMenuItem onClick={onEditRealized}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar Realizado
                          </DropdownMenuItem>
                        )}
                        {onDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={onDelete} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Badge className={cn('text-[10px] sm:text-xs', statusInfo.color)}>
                  {statusInfo.label}
                </Badge>
                <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(targetDate, "dd/MM/yyyy", { locale: ptBR })}
                </span>
                <span className="text-[10px] sm:text-xs text-muted-foreground">
                  ({monthsRemaining} meses restantes)
                </span>
              </div>

              {/* Progress bar */}
              <div className="pt-1">
                <ProgressIndicator
                  value={currentValue}
                  max={targetAmount}
                  showPercentage={false}
                  size="sm"
                  colorScheme={progress >= 100 ? 'success' : 'auto'}
                />
              </div>
            </div>
          </div>

          {/* Bottom/Right section - Amounts and actions */}
          <div className="flex items-center sm:items-start justify-between sm:justify-end gap-2 sm:gap-3 pl-7 sm:pl-0">
            <div className="text-left sm:text-right space-y-0.5">
              <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Objetivo:</span>
                <CurrencyDisplay value={targetAmount} size="sm" />
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Acumulado:</span>
                <CurrencyDisplay
                  value={currentValue}
                  size="sm"
                  colorize
                />
              </div>
              {expense.last_investment_date && (
                <div className="flex items-center gap-1 sm:gap-1.5 sm:justify-end text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span className="text-[10px] sm:text-xs">
                    {format(parseISO(expense.last_investment_date), "dd/MM/yyyy", { locale: ptBR })}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                <span className="text-[10px] sm:text-xs text-muted-foreground">A Investir:</span>
                <CurrencyDisplay value={monthlyPayment} size="xs" />
              </div>
            </div>

            {/* Botão de investir destacado para ativas */}
            {isActive && onRealize && !hasInvestedInCurrentPeriod && (
              <Button
                variant="default"
                size="sm"
                onClick={onRealize}
                disabled={isRealizeDisabled}
                data-tour-id={realizeTourId}
                className={cn(
                  "h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm font-medium shrink-0",
                  isRealizeDisabled ? "bg-muted text-muted-foreground" : "bg-primary hover:bg-primary/90"
                )}
              >
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Investir
              </Button>
            )}

            {/* Actions - desktop only */}
            <div className="hidden sm:block">
              {shouldShowActionDialog ? (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="h-8 px-3 font-medium"
                  onClick={() => setIsPositiveBalanceDialogOpen(true)}
                >
                  <MoreVertical className="h-4 w-4 mr-1" />
                  Ações
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {isActive ? (
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações da meta">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="default" size="sm" className="h-8 px-3 font-medium">
                        <MoreVertical className="h-4 w-4 mr-1" />
                        Ações
                      </Button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onEdit && (
                      <DropdownMenuItem onClick={onEdit}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar Planejado
                      </DropdownMenuItem>
                    )}
                    {onEditRealized && hasInvestedInCurrentPeriod && (
                      <DropdownMenuItem onClick={onEditRealized}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar Realizado
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={onDelete} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </CardContent>

      {/* Dialog de ação para saldo positivo */}
      <Dialog
        open={isPositiveBalanceDialogOpen}
        onOpenChange={(open) => {
          setIsPositiveBalanceDialogOpen(open);
          if (open) setPositiveBalanceAction('replan');
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Planejar Despesa</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-base font-medium text-foreground">{actionDialogMessage}</p>
            <p className="text-sm text-muted-foreground">Como gostaria de prosseguir?</p>
            <RadioGroup
              value={positiveBalanceAction}
              onValueChange={(value) => setPositiveBalanceAction(value as 'replan' | 'revenue' | 'delete')}
              className="grid gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="replan" id="positive-replan" />
                <Label htmlFor="positive-replan">Replanejar</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="revenue" id="positive-revenue" />
                <Label htmlFor="positive-revenue">Adicionar como Receita</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="delete" id="positive-delete" />
                <Label htmlFor="positive-delete">Excluir</Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground italic">*Esta ação não poderá ser desfeita</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPositiveBalanceDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setIsPositiveBalanceDialogOpen(false);
                if (positiveBalanceAction === 'replan') {
                  onReplanFromSurplus?.();
                  return;
                }
                if (positiveBalanceAction === 'revenue') {
                  onAddAsRevenue?.();
                  return;
                }
                onDeleteFromSurplus?.();
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default FutureExpenseRow;


